import React from 'react';
import {Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Flex} from 'src/design/atoms/Flex';
import {Text} from 'src/design/atoms/Text';
import type {MySkillStatusResponse} from 'src/api/ProactApiClient';
import {SkillMatrixStatusEnum} from 'src/api/ProactApiClient';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

const STATUS_PILL: Record<
  SkillMatrixStatusEnum,
  {text: string; color?: string}
> = {
  [SkillMatrixStatusEnum.Never]: {text: 'Never', color: 'gray'},
  [SkillMatrixStatusEnum.SignedOff]: {text: 'Signed off', color: 'green'},
  [SkillMatrixStatusEnum.Expiring]: {text: 'Expiring', color: 'yellow'},
  [SkillMatrixStatusEnum.Expired]: {text: 'Expired', color: 'red'},
};

const skillSubtitle = (item: MySkillStatusResponse): string | undefined => {
  if (item.status === SkillMatrixStatusEnum.Never || !item.expiresAt) {
    return undefined;
  }
  return `Expires ${parseUtcDate(item.expiresAt).toLocaleDateString()}`;
};

export interface SkillItemProps {
  skill: MySkillStatusResponse;
  isLast: boolean;
}

/** One row in the My Skills card: skill name, expiry subtitle, status badge. */
export const SkillItem: React.FC<SkillItemProps> = ({skill, isLast}) => {
  const pill = STATUS_PILL[skill.status] ?? {text: 'Unknown', color: 'gray'};
  const subtitle = skillSubtitle(skill);

  return (
    <>
      <Box w="100%" py="xs" px="xs">
        <Flex gap="sm" align="flex-start" justify="space-between">
          <Box style={{flex: 1, minWidth: 0}}>
            <Text size="sm" fw={500} lineClamp={1}>
              {skill.skillName}
            </Text>
            {subtitle && (
              <Text size="xs" c="dimmed" mt={4}>
                {subtitle}
              </Text>
            )}
          </Box>
          <Badge size="sm" color={pill.color} variant="light">
            {pill.text}
          </Badge>
        </Flex>
      </Box>
      {!isLast && <Divider />}
    </>
  );
};
